import { Conversation } from "../types/Conversation.ts";
import { apiGet, apiPut } from "./api.ts";

export interface ConversationFilters {
  rating?: number[];
  adminRating?: number[];
  hasAdminComment?: "yes" | "no";
  hasUserComment?: "yes" | "no";
  evaluation?: "yes" | "no";
  useForTraining?: "yes" | "no";
  depth0?: string[];
  depth1?: string[];
  depth2?: string[];
  depth3?: string[];
  depth4?: string[];
}

export interface ConversationFilterOptions {
  depth0: string[];
  depth1: string[];
  depth2: string[];
  depth3: string[];
  depth4: string[];
}

export type { ConversationFilters as ApiFilters };

export async function fetchConversationFilters(): Promise<ConversationFilterOptions> {
  return apiGet<ConversationFilterOptions>("/conversations/filters");
}

export async function fetchConversations(filters?: ConversationFilters): Promise<{ data: Conversation[] }> {
  const params = new URLSearchParams();
  
  if (filters) {
    if (filters.rating && filters.rating.length > 0) {
      filters.rating.forEach(r => params.append("rating", r.toString()));
    }
    if (filters.adminRating && filters.adminRating.length > 0) {
      filters.adminRating.forEach(r => params.append("adminRating", r.toString()));
    }
    if (filters.hasAdminComment) {
      params.append("hasAdminComment", filters.hasAdminComment);
    }
    if (filters.hasUserComment) {
      params.append("hasUserComment", filters.hasUserComment);
    }
    if (filters.evaluation) {
      params.append("evaluation", filters.evaluation);
    }
    if (filters.useForTraining) {
      params.append("useForTraining", filters.useForTraining);
    }
    if (filters.depth0 && filters.depth0.length > 0) {
      filters.depth0.forEach(d => params.append("depth0", d));
    }
    if (filters.depth1 && filters.depth1.length > 0) {
      filters.depth1.forEach(d => params.append("depth1", d));
    }
    if (filters.depth2 && filters.depth2.length > 0) {
      filters.depth2.forEach(d => params.append("depth2", d));
    }
    if (filters.depth3 && filters.depth3.length > 0) {
      filters.depth3.forEach(d => params.append("depth3", d));
    }
    if (filters.depth4 && filters.depth4.length > 0) {
      filters.depth4.forEach(d => params.append("depth4", d));
    }
  }
  
  return apiGet<{ data: Conversation[] }>(`/conversations?${params.toString()}`);
}

export async function fetchConversation(id: number): Promise<Conversation> {
  return apiGet<Conversation>(`/conversations/${id}`);
}

export interface ConversationEvaluation {
  comment?: string;
  rating?: number;
  useForTraining?: boolean | null;
}

export async function fetchConversationEvaluation(id: number): Promise<ConversationEvaluation> {
  return apiGet<ConversationEvaluation>(`/conversations/${id}/admin-feedback/me`);
}

export async function updateConversationEvaluation(
  id: number,
  payload: {
    adminComment?: string;
    adminRating?: number;
    useForTraining?: boolean | null;
    rating?: number;
  }
): Promise<ConversationEvaluation> {
  const apiPayload = {
    ...payload,
    rating: payload.rating ?? payload.adminRating,
  };
  return apiPut<ConversationEvaluation, typeof apiPayload>(
    `/conversations/${id}/admin-feedback/me`,
    apiPayload
  );
}

export interface AdminFeedbackEntry {
  id: number;
  adminId: string;
  adminName?: string;
  comment: string;
  rating?: number;
  useToTrain: boolean;
  createdAt: string;
  updatedAt: string;
}

export async function fetchAdminFeedbackHistory(id: number): Promise<{ data: AdminFeedbackEntry[] }> {
  return apiGet<{ data: AdminFeedbackEntry[] }>(`/conversations/${id}/admin-feedback`);
}